import { TableInstructionsText } from '@dealstack/frontend/investor/pages/Investment/common/TableInstructionsText';
import {
	SIGNED_CHIP_VARIANT,
	SignedChip,
} from '@dealstack/frontend/shared/components/Chips/CommonChips';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { TableRowActionsMenu } from '@dealstack/frontend/shared/components/TableRowActionsMenu/TableRowActionsMenu';
import { TableRowActionOption } from '@dealstack/frontend/shared/components/TableRowActionsMenu/TableRowActionsMenu.types';
import { INVESTOR_TEST_ELEMENT_ID } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { isKycRejected } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/utils.ts';
import { Button, Flex, Text } from '@mantine/core';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import { KycFormSignAction } from './KycFormSignAction';
import { canSignKycForm, isKycFormSigned } from './KycFormSignAction.utils';
import { FormPair, hasSubmission } from './types';

const getSignedChipVariant = (form: FormPair): SIGNED_CHIP_VARIANT => {
	// Nothing uploaded yet: "Not Signed" here read as a signing step, when the investor still has
	// to download, fill and upload the form.
	if (!hasSubmission(form)) {
		return SIGNED_CHIP_VARIANT.NOT_UPLOADED;
	}
	if (isKycRejected(form.status)) {
		return SIGNED_CHIP_VARIANT.REJECTED;
	}
	// A signing form that has been submitted but not yet signed is not "Signed" -
	// showing the green chip here contradicted the Sign action offered in the same row.
	if (form.requiresSignature && !isKycFormSigned(form)) {
		return SIGNED_CHIP_VARIANT.NOT_SIGNED;
	}
	return SIGNED_CHIP_VARIANT.SIGNED;
};

interface Props {
	tInvestor: TranslateFunction<I18Namespaces.INVESTOR>;
	onDelete: (templateId: string) => void;
	onPreview: (formPair: FormPair) => void;
	onSign: (formPair: FormPair) => void;
	onInstructionClick: (templateId: string) => void;
	onStartSigning: (formPair: FormPair) => void;
}

export const getKycFormsTableColumns = ({
	tInvestor,
	onDelete,
	onPreview,
	onSign,
	onInstructionClick,
	onStartSigning,
}: Props): ExtendedColumnDef<FormPair>[] => [
	createTextColumn({
		id: 'name',
		headerText: tInvestor('investment.join.kyc.kyc_forms.name'),
		accessorFn: (row) => row.fileName || row.name,
		enableSorting: false,
	}),
	{
		id: 'status',
		header: () => <Text fw={600}>{tInvestor('investment.join.kyc.kyc_forms.status')}</Text>,
		enableSorting: false,
		cell: ({ row }) => <SignedChip variant={getSignedChipVariant(row.original)} />,
		size: 200,
		minSize: 200,
		maxSize: 200,
	},
	{
		id: 'instructions',
		header: () => (
			<Text fw={600}>{tInvestor('investment.join.kyc.kyc_forms.instructions')}</Text>
		),
		enableSorting: false,
		cell: ({ row }) => (
			<TableInstructionsText
				onClick={() => onInstructionClick(row.original.templateId)}
				instruction={row.original.instruction}
			/>
		),
	},
	{
		id: 'action',
		header: () => <Text fw={600}>{tInvestor('investment.join.kyc.kyc_forms.action')}</Text>,
		enableSorting: false,
		size: 200,
		minSize: 200,
		cell: ({ row }) => {
			const form = row.original;

			// KycFormSignAction owns "Sign"; the upload affordance below applies only to a form
			// never submitted or since rejected. A done form gets no primary action at all.
			const showsSignAction = canSignKycForm(form);
			const showsUploadAction =
				!showsSignAction && (!hasSubmission(form) || isKycRejected(form.status));

			return (
				<Flex gap="xs" align="center">
					{/*
						Trigger only — the eID Easy modal is a page-level sibling (useKycFormSigning),
						not a cell child, so the post-signing refetch can't unmount it.
					*/}
					<KycFormSignAction form={form} onStartSigning={onStartSigning} />
					{showsUploadAction && (
						<Button
							data-testid={
								!hasSubmission(form)
									? INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_FORM_COMPLETE_FORM_BUTTON
									: undefined
							}
							size="md"
							variant="subtle"
							onClick={() => onSign(form)}
						>
							{tInvestor(
								hasSubmission(form)
									? 'investment.join.kyc.proof_box.upload_new'
									: 'investment.join.contracts.row.form'
							)}
						</Button>
					)}
				</Flex>
			);
		},
	},
	{
		// Own header-less column so the three-dots lands at a fixed x-position regardless of the
		// primary action's text length — same pattern as fundsTableColumns.tsx.
		id: 'actionsMenu',
		header: '',
		enableSorting: false,
		size: 80,
		cell: ({ row }) => {
			const form = row.original;

			// Remove only applies once something is submitted — and stays available unsigned, so a
			// wrong file can be replaced without signing it first.
			const menuOptions: TableRowActionOption[] = [
				{
					label: tInvestor('investment.join.contracts.column.view'),
					onClick: () => onPreview(form),
					dataTestId: TEST_ELEMENT_IDS.TABLE_ROW_ACTION_VIEW,
				},
				...(hasSubmission(form)
					? [
							{
								label: tInvestor('investment.join.kyc.kyc_forms.sign_kyc.remove'),
								onClick: () => onDelete(form.templateId),
							},
						]
					: []),
			];

			return <TableRowActionsMenu options={menuOptions} />;
		},
	},
];
